/**
 * Persistent auth storage for the native Capacitor app.
 *
 * Problem: the app stores the auth token (`uid`) and `user_id` in cookies via
 * js-cookie. The Android WebView does NOT reliably persist JavaScript cookies
 * across app restarts, so users got logged out every time they reopened the app
 * (web stays logged in fine).
 *
 * Fix: on native we ALSO mirror those two values into @capacitor/preferences
 * (real native key/value storage that survives restarts). On app launch we
 * rehydrate the cookies from Preferences before React renders, so the rest of
 * the app keeps reading them with Cookies.get() exactly as before.
 *
 * On the web these helpers are no-ops — cookies already persist there.
 */
import { Capacitor } from '@capacitor/core';
import Cookies from 'js-cookie';
import { Preferences } from '@capacitor/preferences';

const COOKIE_OPTS = { expires: 7, secure: true, sameSite: 'strict' };

/** Persist the auth pair to native storage (native only). */
export async function saveAuth(uid, userId) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (uid != null) await Preferences.set({ key: 'uid', value: String(uid) });
    if (userId != null) await Preferences.set({ key: 'user_id', value: String(userId) });
  } catch (e) {
    console.error('[authStorage] saveAuth failed:', e?.message || e);
  }
}

/** Remove the persisted auth pair (native only) — call on logout. */
export async function clearAuth() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Preferences.remove({ key: 'uid' });
    await Preferences.remove({ key: 'user_id' });
  } catch (e) {
    console.error('[authStorage] clearAuth failed:', e?.message || e);
  }
}

/**
 * On native launch, copy uid/user_id from Preferences back into cookies if the
 * cookies were lost. Safe to await before rendering. No-op on web.
 */
export async function restoreAuthFromPreferences() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (!Cookies.get('uid')) {
      const { value } = await Preferences.get({ key: 'uid' });
      if (value) Cookies.set('uid', value, COOKIE_OPTS);
    }
    if (!Cookies.get('user_id')) {
      const { value } = await Preferences.get({ key: 'user_id' });
      if (value) Cookies.set('user_id', value, COOKIE_OPTS);
    }
  } catch (e) {
    console.error('[authStorage] restoreAuthFromPreferences failed:', e?.message || e);
  }
}
